import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import { getAppointmentsTableReportData, getAppointmentAnalyticsReportData } from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { serviceTypes, typeList, visitTypes } from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./AppointmentAnalytics.css";

const tableHdrs = [
  "appointmentDateTime",
  "vetName",
  "petName",
  "caseId",
  "typeOfConsult",
  "serviceType",
  "visitType",
  "timeSpent",
  "delay",
  "waitTime",
];
const initSearch = {
  vetName: "",
  petName: "",
  caseId: "",
  typeOfConsult: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Appointment Date",
  "Service Type",
  "Visit Type",
  "Time Spent",
  "Delay",
  "Wait Time",
];
const dropFields = [
  "Service Type",
  "Visit Type",
];

const AppointmentsTabTableReport = () => {
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState("2025-08 to 2026-08");
  const [selectedConsultMode, setSelectedConsultMode] = useState("all");
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { appointmentTable, appointmentAnalytics } = useSelector((state) => state?.report || {});
  const rowsPerPage = 10;

  const getDatesFromRange = (rangeStr) => {
    if (!rangeStr) return { startDate: "2025-08-01", endDate: "2026-08-31" };
    const parts = rangeStr.split(" to ");
    if (parts.length === 2) {
      const start = moment(parts[0], "YYYY-MM").startOf("month").format("YYYY-MM-DD");
      const end = moment(parts[1], "YYYY-MM").endOf("month").format("YYYY-MM-DD");
      return { startDate: start, endDate: end };
    }
    return { startDate: "2025-08-01", endDate: "2026-08-31" };
  };

  useEffect(() => {
    const { startDate, endDate } = getDatesFromRange(dateRange);
    dispatch(
      getAppointmentAnalyticsReportData({
        startDate,
        endDate,
        consultationMode: selectedConsultMode,
      })
    );
  }, [dispatch, dateRange, selectedConsultMode]);

  useEffect(() => {
    onApiCall(1);
  }, [
    searchValues?.vetName,
    searchValues?.petName,
    searchValues?.caseId,
    searchValues?.typeOfConsult,
  ]);

  const analyticsPayload = appointmentAnalytics?.data || appointmentAnalytics || {};
  const cards = analyticsPayload?.cards || {};

  const summaryCards = [
    {
      title: "Physical",
      value:
        cards?.physicalPercentage !== undefined
          ? `${cards.physicalPercentage}%`
          : "88%",
    },
    {
      title: "Virtual",
      value:
        cards?.virtualPercentage !== undefined
          ? `${cards.virtualPercentage}%`
          : "12%",
    },
    {
      title: "Avg time spent",
      value: cards?.avgTimeSpentDisplay ?? (cards?.avgTimeSpentMinutes !== undefined ? `${Math.round(cards.avgTimeSpentMinutes)} min` : "18 min"),
    },
    {
      title: "Avg wait time",
      value: cards?.avgWaitTimeDisplay ?? (cards?.avgWaitTimeMinutes !== undefined ? `${Math.round(cards.avgWaitTimeMinutes)} min` : "14 min"),
    },
  ];

  // Donut Chart: Appointment type mix
  const rawTypeMix = Array.isArray(analyticsPayload?.appointmentTypeMix)
    ? analyticsPayload.appointmentTypeMix
    : [];

  const typeMixLabels =
    rawTypeMix.length > 0
      ? rawTypeMix.map((item) => item?.label || "Other")
      : ["Checkups", "Vaccinations", "Grooming", "Surgeries", "Emergency/Others"];

  const typeMixSeries =
    rawTypeMix.length > 0
      ? rawTypeMix.map((item) =>
          typeof item?.percentage === "number" && !isNaN(item?.percentage)
            ? item.percentage
            : typeof item?.count === "number" && !isNaN(item?.count)
            ? item.count
            : 0
        )
      : [45, 25, 15, 10, 5];

  const typeMixChartOptions = {
    chart: { type: "donut", toolbar: { show: false } },
    labels: typeMixLabels,
    colors: ["#0F172A", "#2563EB", "#60A5FA", "#CBD5E1", "#F59E0B", "#10B981", "#8B5CF6"],
    plotOptions: { pie: { donut: { size: "60%" } } },
    dataLabels: { enabled: false },
    legend: { position: "bottom", horizontalAlign: "center", fontFamily: "Montserrat, sans-serif", fontSize: "12px", markers: { width: 10, height: 10, radius: 2 } },
  };

  const typeMixChartSeries = typeMixSeries;

  // Bar Chart: Patient wait time by month
  const rawWaitTime = Array.isArray(analyticsPayload?.patientWaitTimeByMonth)
    ? analyticsPayload.patientWaitTimeByMonth
    : [];

  const waitTimeCategories =
    rawWaitTime.length > 0
      ? rawWaitTime.map((item) => `${item?.month || ""} ${item?.year ? String(item.year).slice(-2) : ""}`)
      : ["Aug 25", "Sep 25", "Oct 25", "Nov 25", "Dec 25", "Jan 26", "Feb 26", "Mar 26", "Apr 26", "May 26", "Jun 26", "Jul 26", "Aug 26"];

  const waitTimeSeriesData =
    rawWaitTime.length > 0
      ? rawWaitTime.map((item) => (typeof item?.avgWaitMinutes === "number" && !isNaN(item?.avgWaitMinutes) ? Math.round(item.avgWaitMinutes * 10) / 10 : 0))
      : [0, 0, 0, 0, 0, 0, 623, 78, 90, 65, 632, 112, 21];

  const waitTimeChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#2563EB"],
    plotOptions: { bar: { borderRadius: 4, columnWidth: "50%" } },
    dataLabels: { enabled: false },
    xaxis: { categories: waitTimeCategories, labels: { style: { fontSize: "11px", fontFamily: "Montserrat, sans-serif" } } },
    yaxis: { labels: { formatter: (val) => `${val}m` } },
  };

  const waitTimeChartSeries = [
    { name: "Avg Wait Time", data: waitTimeSeriesData },
  ];

  const handleExportExcel = () => {
    const tableData = appointmentTable?.data ?? [];
    const exportData = tableData.map((row) => ({
      "Date & time": row?.appointmentDateTime
        ? moment(new Date(row?.appointmentDateTime)).format("DD MMM YYYY, HH:mm")
        : "",
      Vet: row?.vetName ?? "",
      Pet: row?.petName ?? "",
      "Case ID": row?.caseId ?? "",
      "Consultation mode": row?.typeOfConsult ?? "",
      "Service type": row?.serviceType ?? "",
      "Visit type": row?.visitType ?? "",
      "Time spent": row?.timeSpent ?? "",
      "Wait time": row?.waitTime ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Appointment Analytics");
    XLSX.writeFile(workbook, `Appointment_Analytics_Report_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportPDF = () => {
    window.print();
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = {
      type: "appointments",
      page: currentPage,
      itemSize: rowsPerPage,
    };
    if (reset) return reqObj;
    if (searchValues?.vetName?.length > 0) reqObj.vetName = [searchValues?.vetName];
    if (searchValues?.petName?.length > 0) reqObj.petName = [searchValues?.petName];
    if (searchValues?.caseId?.length > 0) reqObj.caseId = [searchValues?.caseId];
    if (searchValues?.typeOfConsult?.length > 0) reqObj.typeOfConsult = searchValues?.typeOfConsult?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Appointment Date")) {
      reqObj.startDate = moment(new Date(filterValues?.values?.["Start Date"] ?? new Date())).format("YYYY-MM-DD");
      reqObj.endDate = moment(new Date(filterValues?.values?.["End Date"] ?? new Date())).format("YYYY-MM-DD");
    }
    if (filterValues?.selectedList?.includes("Service Type")) reqObj.serviceType = filterValues?.values?.["Service Type"]?.map((li) => li?.value);
    if (filterValues?.selectedList?.includes("Visit Type")) reqObj.visitType = filterValues?.values?.["Visit Type"]?.map((li) => li?.value);
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    dispatch(getAppointmentsTableReportData(getFilterObj(pge, reset)));
  };

  const getDropListByType = (typ) => {
    return typ === "Service Type" ? serviceTypes : typ === "Visit Type" ? visitTypes : [];
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find((tblHdr) => tblHdr?.name === pd);
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => setEditColModVsble(false);

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders?.filter((act) => act?.isSelected)?.map((act) => act?.name);
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((appointmentTable?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => onApiCall(selectedPage);

  const getTableData = () => {
    return (appointmentTable?.data ?? [])?.map((li) => ({
      ...li,
      appointmentDateTime:
        li?.appointmentDateTime?.length > 0
          ? moment(new Date(li?.appointmentDateTime)).format("DD MMM YYYY, HH:mm")
          : null,
      typeOfConsult: li?.typeOfConsult === "Home" ? "Home Visit" : li?.typeOfConsult,
    }));
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({ ...searchValues, [name?.trim()?.replace(/,$/, "")]: value });
  };

  const onFilterChange = (name, value) => setFilterValues({ ...filterValues, [name]: value });

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
    flushSync(() => setFilterValues({ ...initFilter, selectedList: [], values: {} }));
    onApiCall(1, true);
  };

  const getVetList = () => uniq((appointmentTable?.data ?? [])?.map((li) => li?.vetName)?.filter(Boolean));
  const getPetList = () => uniq((appointmentTable?.data ?? [])?.map((li) => li?.petName)?.filter(Boolean));
  const getCaseIds = () => uniq((appointmentTable?.data ?? [])?.map((li) => li?.caseId?.toString())?.filter(Boolean));

  const onApplyFilter = () => {
    onApiCall();
    setFilterValues({ ...filterValues, visible: false });
  };

  const onCloseFilter = () => setFilterValues({ ...filterValues, visible: false });

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    setFilterValues({ ...filterValues, values: { ...filterValues?.values, [name?.trim()?.replace(/,$/, "")]: value } });
  };

  const onFilterAdd = (value) => setFilterValues({ ...filterValues, added: value });

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) reqList = reqList?.filter((item) => item !== name);
    else reqList.push(name);
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterReset = () => setFilterValues({ ...initFilter, visible: true, selectedList: [], values: {} });

  return (
    <div className="appointment-analytics-container">
      {/* Title */}
      <h2 className="appointment-analytics-title">Appointment analytics</h2>

      {/* Top Filter Bar */}
      <div className="appointment-analytics-filter-bar">
        <Select
          size="small"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="2025-08 to 2026-08">2025-08 to 2026-08</MenuItem>
          <MenuItem value="2026-01 to 2026-08">2026-01 to 2026-08</MenuItem>
        </Select>

        <Select
          size="small"
          value={selectedConsultMode}
          onChange={(e) => setSelectedConsultMode(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="all">All consultation modes</MenuItem>
          <MenuItem value="physical">Physical</MenuItem>
          <MenuItem value="virtual">Virtual</MenuItem>
        </Select>
      </div>

      {/* Export Action Buttons */}
      <div className="appointment-analytics-export-group">
        <button type="button" className="appointment-analytics-export-btn" onClick={handleExportExcel}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export Excel
        </button>
        <button type="button" className="appointment-analytics-export-btn" onClick={handleExportPDF}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export PDF
        </button>
      </div>

      {/* Summary Cards Grid (4 Cards) */}
      <div className="appointment-analytics-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="appointment-analytics-card" key={idx}>
            <h4 className="appointment-analytics-card-title">{card.title}</h4>
            <div className="appointment-analytics-card-value-container">
              <h2 className="appointment-analytics-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Side-by-side Grid: Type Mix + Patient Wait Time */}
      <div className="appointment-analytics-charts-grid">
        <div className="appointment-analytics-chart-card">
          <div className="appointment-analytics-chart-header">
            Appointment type mix
          </div>
          <Chart
            options={typeMixChartOptions}
            series={typeMixChartSeries}
            type="donut"
            height={300}
          />
        </div>

        <div className="appointment-analytics-chart-card">
          <div className="appointment-analytics-chart-header">
            Patient wait time, by month
          </div>
          <Chart
            options={waitTimeChartOptions}
            series={waitTimeChartSeries}
            type="bar"
            height={300}
          />
        </div>
      </div>

      {/* Drill-down Section */}
      <div className="appointment-analytics-drilldown-header">Drill-down — appointments</div>

      {/* Search Filters Row */}
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getVetList()}
              value={searchValues?.vetName ?? []}
              name="vetName"
              handleChange={onSearchChange}
              label="Vet Name"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getPetList()}
              value={searchValues?.petName ?? []}
              name="petName"
              handleChange={onSearchChange}
              label="Pet Name"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getCaseIds()}
              value={searchValues?.caseId ?? []}
              name="caseId"
              handleChange={onSearchChange}
              label="Case ID"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={typeList}
              value={searchValues?.typeOfConsult ?? []}
              name="typeOfConsult"
              handleChange={onSearchChange}
              label="Consultation Mode"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0 ? `${filterValues?.selectedList?.length} ` : ""}Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {appointmentTable?.totalRecords ?? 0}
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((appointmentTable?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal open={editColModVsble} onClose={onEditModClose} header="Columns" modal modalWidth={25}>
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton text="Save" smallBtn onClick={handleSaveTableColumn} />
          </div>
        </div>
      </CustomModal>

      {/* Filter Modal */}
      <CustomModal open={filterValues?.visible} onClose={onCloseFilter} header="Filter" rightModal modalWidth={30}>
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="gray38 fs12 txt-regular">Filter by:</Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <CustomCheckbox
                      checked={filterValues?.selectedList?.includes(li)}
                      onChange={() => onSelectFilter(li)}
                    />
                    <Typography className="txt-regular fs14 black mb5 mlMin15">{li}</Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor" onClick={() => onFilterAdd(false)}>
                    <AddIcon sx={{ color: AppColors.appPrimary, fontSize: 15 }} />
                    <Typography className="font-medium fs12 blue-color ml5">Add Filter</Typography>
                  </div>
                  {filterValues?.selectedList?.map((li) => (
                    <div className="mt20" key={li}>
                      {dropFields?.includes(li) ? (
                        <CustomSelect
                          list={getDropListByType(li)}
                          value={filterValues?.values?.[li] ?? []}
                          name={li}
                          handleChange={onFilterValuesChange}
                          label={li}
                          multiSelectSearchCheck
                        />
                      ) : (
                        <CustomTextField
                          placeholder={li}
                          fullWidth
                          name={li}
                          handleChange={onFilterValuesChange}
                          value={filterValues?.values?.[li] ?? ""}
                          label={li}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <CustomButton text="Add" smallBtn onClick={() => onFilterAdd(true)} />
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <CustomButton text="Reset All" smallBtn onClick={onFilterReset} tabSelectdBtn />
                  <div className="ml20">
                    <CustomButton text="Apply Filters" smallBtn onClick={onApplyFilter} />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </div>
  );
};

export default AppointmentsTabTableReport;
